import { useEffect, useMemo, useState } from "react";
import {
  CATEGORY_LABELS,
  SYMBOL_REGISTRY,
  type SymbolEntry,
} from "./symbols/symbolRegistry.tsx";

/**
 * Symbolpalett. Visar alla elcentral-symboler i ett rutnät grupperat per
 * kategori. Klick på en symbol kallar onInsert(shortCode) vilket skapar en
 * ny rad i tabellen (PanelEditor håller ihop flödet).
 *
 * Våra egna ritningar — inte kopierade från enlinje.se.
 */
export function SymbolPickerDialog({
  open,
  onClose,
  onInsert,
}: {
  open: boolean;
  onClose: () => void;
  onInsert: (shortCode: string) => void;
}) {
  const [filter, setFilter] = useState("");

  useEffect(() => {
    if (!open) {
      setFilter("");
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const grouped = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    const filtered = needle
      ? SYMBOL_REGISTRY.filter(
          (s) =>
            s.label.toLowerCase().includes(needle) ||
            s.shortCode.toLowerCase().includes(needle),
        )
      : SYMBOL_REGISTRY;
    const map = new Map<SymbolEntry["category"], SymbolEntry[]>();
    for (const sym of filtered) {
      const arr = map.get(sym.category) ?? [];
      arr.push(sym);
      map.set(sym.category, arr);
    }
    return map;
  }, [filter]);

  if (!open) return null;

  const pick = (shortCode: string) => {
    onInsert(shortCode);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
      onClick={onClose}
    >
      <div
        className="flex max-h-[85vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <div>
            <h2 className="text-lg font-semibold text-slate-800">Symbolpalett</h2>
            <p className="text-xs text-slate-500">
              Klicka på en symbol för att lägga till som ny rad i tabellen.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded px-2 py-1 text-slate-500 hover:bg-slate-100"
            aria-label="Stäng"
          >
            ×
          </button>
        </div>

        <div className="border-b border-slate-200 px-4 py-2">
          <input
            type="search"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Sök symbol (t.ex. dvärg, JFB, kontaktor) …"
            autoFocus
            className="w-full rounded border border-slate-300 px-3 py-1.5 text-sm focus:border-sky-400 focus:outline-none"
          />
        </div>

        <div className="flex-1 overflow-auto p-4">
          {grouped.size === 0 ? (
            <div className="py-8 text-center text-sm text-slate-500">
              Inga symboler matchar ”{filter}”.
            </div>
          ) : (
            <div className="space-y-6">
              {(Object.keys(CATEGORY_LABELS) as SymbolEntry["category"][]).map(
                (cat) => {
                  const items = grouped.get(cat);
                  if (!items || items.length === 0) return null;
                  return (
                    <section key={cat}>
                      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                        {CATEGORY_LABELS[cat]}
                      </h3>
                      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
                        {items.map((s) => (
                          <SymbolCard
                            key={s.shortCode}
                            entry={s}
                            onPick={() => pick(s.shortCode)}
                          />
                        ))}
                      </div>
                    </section>
                  );
                },
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SymbolCard({
  entry,
  onPick,
}: {
  entry: SymbolEntry;
  onPick: () => void;
}) {
  const Icon = entry.icon;
  return (
    <button
      type="button"
      onClick={onPick}
      className="group flex flex-col items-center gap-1 rounded border border-slate-200 bg-white px-2 py-3 text-center transition hover:border-sky-400 hover:bg-sky-50"
      title={`${entry.label} (kortkod: ${entry.shortCode})`}
    >
      <div className="text-slate-700 group-hover:text-sky-700">
        <Icon size={44} />
      </div>
      <div className="text-xs font-medium text-slate-700">{entry.label}</div>
      <div className="font-mono text-[10px] text-slate-400">
        {entry.shortCode}
      </div>
    </button>
  );
}
